import {FC} from "react"
import {Trans} from "react-i18next"

import {AddModelNumberModal} from "@frontend/components/add-model-number-modal"
import {trackEvent} from "@frontend/utils/trackEvent"
import {useBooleanState} from "@frontend/utils/useBooleanState"
import type {UiCase} from "@ri2/app/server/routers/getCasePageData"
import {css} from "@styled-system/css"
import {vstack} from "@styled-system/patterns"

interface Props {
  caseModel: UiCase
}

export const CaseHeader: FC<Props> = ({
  caseModel: {id, name, modelDescription, modelNumber},
}) => {
  const {
    setTrue: onShowAddModelNumberModal,
    setFalse: onHideAddModelNumberModal,
    state: showAddModelNumberModal,
  } = useBooleanState(false)

  const onClickShowModelNumberModal = (): void => {
    onShowAddModelNumberModal()
    trackEvent("chat_model_no_tip")
  }

  const displayName = getDisplayName(name)
  const displayModelDescription = normalizeDisplayValue(modelDescription)
  const displayModelNumber = normalizeDisplayValue(modelNumber)

  return (
    <>
      <div
        className={vstack({
          alignItems: {
            base: "center",
            desktop: "flex-start",
          },
          gap: 0,
        })}
      >
        <div
          className={css({
            textStyle: "bodyLargeMedium",
            color: "text.primary",
            lineClamp: 1,
            textAlign: "center",

            desktop: {
              textAlign: "left",
            },
          })}
        >
          {displayName}
        </div>
        <div
          className={css({
            textStyle: "captionRegular",
            lineClamp: 1,
            textAlign: "center",

            desktop: {
              textAlign: "left",
            },
          })}
        >
          {displayModelDescription && (
            <span
              className={css({
                color: "text.quaternary",
              })}
            >
              {displayModelDescription}
            </span>
          )}
          {displayModelDescription && displayModelNumber && (
            <span
              className={css({
                color: "text.quaternary",
              })}
            >
              {" / "}
            </span>
          )}
          {displayModelNumber && (
            <span
              className={css({
                color: "text.brand.accent",
                textStyle: "captionMedium",
              })}
            >
              {displayModelNumber}
            </span>
          )}
          {!displayModelNumber && (
            <span
              className={css({
                color: "text.quaternary",
              })}
            >
              <Trans i18nKey="appContainer.header.noModelNumber">
                <button
                  onClick={onClickShowModelNumberModal}
                  className={css({
                    color: "text.brand.accent",
                    textStyle: "captionMedium",
                  })}
                />
              </Trans>
            </span>
          )}
        </div>
      </div>
      {showAddModelNumberModal && (
        <AddModelNumberModal caseId={id} onClose={onHideAddModelNumberModal} />
      )}
    </>
  )
}

const normalizeDisplayValue = (value: string | null): string | null => {
  if (!value) return null

  const trimmedValue = value.trim()

  if (!trimmedValue) return null

  if (/^(null|unknown|uknown|unknown appliance type)$/i.test(trimmedValue)) {
    return null
  }

  return trimmedValue
}

const getDisplayName = (name: string): string =>
  name
    .trim()
    .replace(/^(?:(?:null|unknown|uknown)\b[\s:/-]*)+/i, "")
    .trim() || "New Case"
